import { useRef, useState } from "react";
import { toast } from "sonner";
import { api, resolveUploadUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Upload, ImageIcon, Trash2, Loader2 } from "lucide-react";

const InvitationUploader = ({ value, onChange, compact = false, title, hint, doneLabel }) => {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const _title = title || "Davetiye görselini seçin";
  const _hint = hint || "PNG, JPG, WebP · en fazla 10 MB";
  const _done = doneLabel || "Davetiye yüklendi";

  const handleFile = async (file) => {
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      toast.error("Dosya 10 MB'ı geçemez");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange?.(data.url);
      toast.success(_done || "Görsel yüklendi");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Yükleme başarısız");
    } finally {
      setUploading(false);
    }
  };

  const remove = () => onChange?.("");

  if (value) {
    return (
      <div className="relative bg-white border-2 border-stone-200 rounded-2xl p-3" data-testid="invitation-preview">
        <img
          src={resolveUploadUrl(value)}
          alt="Davetiye"
          className="w-full rounded-xl object-contain max-h-64 mx-auto"
        />
        <div className="flex items-center justify-between gap-2 mt-3">
          <div className="text-xs text-[#4B5563] flex items-center gap-2">
            <ImageIcon className="w-3.5 h-3.5" />
            {_done}
          </div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={remove}
            data-testid="invitation-remove-btn"
            className="border-stone-300 text-[#7F1D1D] hover:bg-[#7F1D1D]/5 gap-1.5 h-8"
          >
            <Trash2 className="w-3.5 h-3.5" />
            Kaldır
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`bg-white border-2 border-dashed border-stone-300 rounded-2xl text-center hover:border-[#8A252C]/50 transition-colors ${
        compact ? "p-4" : "p-6"
      }`}
      data-testid="invitation-uploader"
    >
      <input
        ref={fileRef}
        type="file"
        accept="image/*"
        hidden
        onChange={(e) => handleFile(e.target.files?.[0])}
        data-testid="invitation-file-input"
      />
      <div className="w-12 h-12 rounded-full bg-[#8A252C]/10 flex items-center justify-center mx-auto mb-2">
        {uploading ? (
          <Loader2 className="w-5 h-5 text-[#8A252C] animate-spin" />
        ) : (
          <Upload className="w-5 h-5 text-[#8A252C]" strokeWidth={1.75} />
        )}
      </div>
      <p className="text-sm font-semibold text-[#1F2937]">
        {uploading ? "Yükleniyor..." : _title}
      </p>
      <p className="text-xs text-[#4B5563] mt-0.5">
        {_hint}
      </p>
      <Button
        type="button"
        onClick={() => fileRef.current?.click()}
        disabled={uploading}
        data-testid="invitation-browse-btn"
        className="mt-3 bg-[#8A252C] hover:bg-[#6f1d24] text-white h-10 rounded-xl gap-2"
      >
        <Upload className="w-4 h-4" />
        Dosya Seç
      </Button>
    </div>
  );
};

export default InvitationUploader;
